import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { useState } from "react";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import ContentCashBalanceRecharge from "./content";
import TableWallet from "../table";
import { useQuery } from "@tanstack/react-query";
import { fetchPayment, fetchWalletBalance } from "@/api/user/home";
import moment from "jalali-moment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Grid, TextField } from "@mui/material";
import ImgStaticCard from "@/shared/components/common/imgStaticCard/imgStaticCard";
import CustomButton from "@/shared/components/kit/button";
import CardBoxLayout from "@/modules/merchant/layout/cardBoxLayout";
import BoxKit from "@/shared/components/kit/box";
import CustomSelectNormal from "@/shared/components/common/select/selectNormal";
import {
  numberSeparator,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";
import useAccountStore from "@/store/balanceStore";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CustomSelect from "@/shared/components/common/select";
import ContentSkeleton from "../content.skeleton";
/**
 * CashBalanceRecharge
 *
 * کامپوننت مدیریت و نمایش شارژ موجودی نقدی کاربر.
 *
 * ویژگی‌ها:
 * - نمایش موجودی نقدی و اعتباری با امکان انتخاب نوع موجودی
 * - شارژ کیف پول از طریق کامپوننت ContentCashBalanceRecharge
 * - نمایش جزئیات آخرین تراکنش‌ها با جدول و فرمت فارسی اعداد و تاریخ‌ها
 * - فیلتر کردن تراکنش‌ها بر اساس انتخاب کاربر
 * - استفاده از React Query برای واکشی داده‌های پرداخت و موجودی
 *
 * @returns JSX.Element
 */
// Task Response:

// نکات و بهینه‌سازی‌ها:
// 1. داده‌های تراکنش با moment و toPersianNumber و numberSeparator فرمت شده‌اند.
// 2. status تراکنش‌ها با آیکون و رنگ‌بندی بر اساس موفقیت یا در حال بررسی نمایش داده می‌شود.
// 3. Grid و StackKit به خوبی برای واکنش‌گرایی (Responsive) استفاده شده است.
// 4. CustomSelectNormal و CustomSelect برای انتخاب نوع موجودی و تعداد تراکنش‌ها استفاده شده‌اند.
// 5. TextField و CustomButton برای جستجوی تراکنش‌ها فراهم شده‌اند.
// 6. جدول TableWallet برای نمایش تراکنش‌ها استفاده شده است.
// 7. تصاویر کارت و پیش‌نمایش با ImgStaticCard نمایش داده شده‌اند.
// 8. بهتر است مقادیر پیش‌فرض در useState با داده‌های واکشی‌شده هماهنگ شوند تا UI بدون خطا render شود.
// 9. اگر تعداد تراکنش‌ها زیاد است، Pagination یا Lazy Load پیشنهاد می‌شود.
// 10. بررسی شود که dataPayment?.payments و dataBalance حتماً وجود دارند قبل از map و دسترسی به property‌ها.

const CashBalanceRecharge = () => {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  const { data: dataPayment, isLoading: isloadingPayment } = useQuery({
    queryKey: ["payment-list-dataPayment", page, size],
    queryFn: () => fetchPayment({ page, size }),
  });

  const { data: dataBalance } = useQuery({
    queryKey: ["wallet-dataBalance-balance"],
    queryFn: fetchWalletBalance,
  });

  const [selected, setSelected] = useState("all");
  const [value, setValue] = useState(10);

  const dataHeader = [
    { id: "amount", title: "مبلغ (ریال)" },
    { id: "updated_at", title: "تاریخ و ساعت" },
    { id: "trackingCode", title: "کد رهگیری تراکنش " },
    { id: "gateway", title: "نام درگاه" },
    { id: "destination", title: "مقصد تراکنش" },
    { id: "status", title: "وضعیت تراکنش" },
  ];

  const { balance, credit, cards } = useAccountStore();
  const dataOptionSelect = [
    {
      label: `موجودی نقدی(ریال) : ${numberSeparator(dataBalance?.balance) ?? 0}`,
      value: "all",
    },
    {
      label: `موجودی اعتباری(ریال) : ${numberSeparator(dataBalance?.credit?.balance) ?? 0}`,
      value: "cash_20card",
    },
  ];

  const handleChange = (val: any) => {
    setValue(val);
  };

  const newData = dataPayment?.payments?.map((item, index) => {
    return {
      ...item,
      amount: numberSeparator(item.amount),
      updated_at: toPersianNumber(`${moment(item.updated_at)
        .locale("fa")
        .format("jYYYY/jMM/jDD")}
        ${moment(item.updated_at).locale("fa").format("HH:MM")}
        `),
      destination: "کیف ساعی پی",
      status:
        item?.status === 0 ? (
          <StackKit direction={"row"} gap={2}>
            <AccessTimeIcon /> درحال بررسی
          </StackKit>
        ) : (
          <StackKit direction={"row"} gap={2}>
            {item.statusText === "پرداخت موفق" ? (
              <FileDownloadOutlinedIcon style={{ color: "green" }} />
            ) : (
              <FileDownloadOutlinedIcon style={{ color: "red" }} />
            )}
            {item.statusText}
          </StackKit>
        ),
    };
  });

  const fakeData = newData;

  // Handle page change from TableWallet
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle items per page change from TableWallet
  const handleItemsPerPageChange = (newSize: number) => {
    setSize(newSize);
    setPage(1); // Reset to first page when changing items per page
  };
  return (
    <StackKit direction={"column"} sx={{ width: "100%" }} gap={2}>
      <StackKit
        direction={"row"}
        sx={{
          justifyContent: { sm: "start", xs: "center" },
          width: "100%",
          gap: "10px",
          alignItems: "center",
        }}
      >
        <AddBoxOutlinedIcon sx={{ color: "#082A2E" }} />
        <TypoGraphyKit sx={{ ml: "0 !important" }} variant="subtitle1">
          شارژ موجودی نقدی
        </TypoGraphyKit>
      </StackKit>
      <Grid
        container
        sx={{
          display: "flex",
          flexDirection: { xs: "column-reverse", md: "row" },
          gap: { xs: 2, sm: 2, md: 0 },
          justifyContent: { xs: "center", md: "space-between" },
          width: "100%",
          alignItems: "center",
        }}
      >
        <Grid size={{ xs: 12, sm: 12, md: 8, lg: 9 }} sx={{ padding: "0 5px" }}>
          <ContentCashBalanceRecharge />
        </Grid>
        <Grid size={3}>
          <BoxKit>
            <StackKit
              className="w-full"
              direction={"column-reverse"}
              alignItems="center"
              sx={{ marginTop: "50px" }}
            >
              <BoxKit sx={{ minWidth: { sm: "250px", xs: "100%" }, mt: 2 }}>
                <CustomSelectNormal
                  label=""
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  options={dataOptionSelect}
                  getOptionLabel={(item) => item.label}
                  getOptionValue={(item) => item.value}
                  width="100%"
                />
              </BoxKit>
              <BoxKit className="w-[180px] h-[180px]" sx={{ mt: 2 }}>
                <ImgStaticCard
                  imgSlider1="/card-out-simple-flat.png"
                  imgSlider2="/card-out-simple-flat2.png"
                  img1Height="180px"
                />
              </BoxKit>
            </StackKit>
          </BoxKit>
        </Grid>
      </Grid>
      <CardBoxLayout
        sx={{
          background: "#fff !important",
          p: 2,
          width: "100%",
          mx: "auto",
        }}
      >
        <StackKit
          direction={"column"}
          gap={2}
          justifyContent="center"
          spacing={2}
        >
          <StackKit
            direction={{ xs: "column", sm: "row" }}
            sx={{ width: "100%" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            paddingY={2}
            spacing={2}
          >
            <TypoGraphyKit variant="h6">شارژ موجودی نقدی:</TypoGraphyKit>
          </StackKit>
        </StackKit>
        {/* Table */}
        {isloadingPayment ? (
          <ContentSkeleton />
        ) : (
          <TableWallet
            dataHeader={dataHeader}
            dataTbody={fakeData || []}
            pagination={{
              current_page: page,
              per_page: size,
              total: dataPayment?.total || 0,
            }}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </CardBoxLayout>
    </StackKit>
  );
};

export default CashBalanceRecharge;
